import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Divider, Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import RecommendationCommentsDrawer from '/@/components/recommendation-comments-drawer';
import RecommendationShare from '/@/components/recommendation-share';
import Spinner from '/@/components/spinner';
import { useBreadcrumbs } from '/@/hooks/breadcrumbs';
import { useGetUserNode } from '/@/hooks/nodes';
import { useRecommendationCommentsPermissions } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import DensityCurve from '/@/views/recommendations/v2/steps/management/density-curve';
import { NutrientTable } from '/@/views/recommendations/v2/steps/management/fertilization-tab';
import HybridDensityCard from '/@/views/recommendations/v2/steps/management/hybrid-density-card';
import PhenologyTimeline, { PhenologyLegend } from '/@/views/recommendations/v2/steps/management/phenology-timeline';
import { useManagementData } from '/@/views/recommendations/v2/steps/management/use-management-data';
import { usePhenology } from '/@/views/recommendations/v2/steps/management/use-phenology';
import GeneralData from './general-data';
import { useSavedRecommendation } from './use-saved-recommendation';

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
    return (
        <Stack spacing={2}>
            <Stack>
                <Typography variant='h6' fontWeight='bold' color='primary'>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant='body2' color='primary'>
                        {subtitle}
                    </Typography>
                )}
            </Stack>
            {children}
        </Stack>
    );
}

/**
 * Read-only view of a saved seed recommendation — where "Terminar" lands.
 *
 * Every section is the component the wizard's last step renders, driven off the same
 * `activeRecommendation` slice, so the two can't drift. Nothing here is editable.
 */
function RecommendationResultV2({
    recommendationId,
    crop = 'corn',
}: {
    recommendationId: number;
    crop?: 'corn' | 'soy';
}) {
    const { recommendation, parsedRecommendation, field, cropConfig, isReady } = useSavedRecommendation({
        recommendationId,
        crop,
    });
    const { data: grower } = useGetUserNode(recommendation?.userNode as number);
    // The case the legacy seed detail uses, so both pages show one breadcrumb trail.
    const breadcrumbs = useBreadcrumbs('recommendation.RecommendationDetail');
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        recommendation?.commentsStatus,
        recommendation?.userNode
    );

    const { hybrids, planterEfficiency, areaInHectares, isLoading } = useManagementData({ field, crop });
    const { hybrids: phenologyHybrids } = usePhenology({ field, crop, plantingDate: cropConfig?.plantingDate });

    const lastUpdated = recommendation?.modified || recommendation?.created;

    const header = (
        <Header
            variant='full-gray'
            breadcrumbs={breadcrumbs}
            title={_('Recomendación de híbrido de maíz')}
            subtitle={
                lastUpdated
                    ? _('Última actualización {{date}}', { date: dayjs(lastUpdated).format('DD/MM/YYYY') })
                    : undefined
            }
            actions={[
                // `disabled` covers the loading window; the prop itself isn't optional.
                parsedRecommendation && (
                    <RecommendationShare
                        key='recommendation-share'
                        type={crop}
                        recommendation={parsedRecommendation}
                        version={2}
                        pdfVersion={2}
                    />
                ),
                recommendation?.id &&
                    shouldShowCommentsButton &&
                    (isSmOrLower ? (
                        <Button
                            key='comments-button'
                            variant='contained'
                            color='primary'
                            fullWidth
                            onClick={() => setCommentsDrawerOpen(true)}
                        >
                            {_('Ver comentarios')}
                        </Button>
                    ) : (
                        <RecommendationCommentsButton
                            key='comments-button'
                            commentsStatus={recommendation.commentsStatus}
                            onOpenDrawer={() => setCommentsDrawerOpen(true)}
                        />
                    )),
            ].filter(Boolean)}
        />
    );

    if (!isReady) {
        return (
            <PageContainer heroHeader={header}>
                <Stack alignItems='center' justifyContent='center' py={10}>
                    <Spinner />
                </Stack>
            </PageContainer>
        );
    }

    return (
        <PageContainer heroHeader={header}>
            <Stack spacing={4} pt={2} pb={8}>
                <GeneralData
                    grower={grower}
                    field={field}
                    plantingDate={cropConfig?.plantingDate}
                    environment={cropConfig?.environment}
                    climatePattern={cropConfig?.climatePattern}
                    soilMoisture={cropConfig?.soilMoisture}
                    crop={crop}
                />

                <Section
                    title={_('Híbridos recomendados')}
                    subtitle={_('Híbridos recomendados según el ambiente y las condiciones del lote.')}
                >
                    {isLoading && !hybrids.length ? (
                        <Stack alignItems='center' py={6}>
                            <Spinner />
                        </Stack>
                    ) : (
                        <>
                            <Box
                                display='grid'
                                gap={2}
                                gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                                alignItems='stretch'
                            >
                                {hybrids.map((hybrid) => (
                                    <HybridDensityCard
                                        key={hybrid.id}
                                        hybrid={hybrid}
                                        planterEfficiency={planterEfficiency}
                                        areaInHectares={areaInHectares}
                                    />
                                ))}
                            </Box>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <InfoOutlinedIcon sx={{ fontSize: 18, color: (t) => t.palette.primary.main }} />
                                <Typography variant='body2' color='primary'>
                                    {_('Densidad de siembra calculada con un porcentaje de logro del {{value}}%.', {
                                        value: planterEfficiency,
                                    })}
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Section>

                <Divider />

                <Section
                    title={_('Densidad objetivo')}
                    subtitle={_(
                        'Cantidad mínima de plantas a cosecha para el máximo rendimiento en el ambiente definido.'
                    )}
                >
                    <DensityCurve hybrids={hybrids} />
                </Section>

                <Divider />

                <Section
                    title={_('Fenología según la fecha de siembra')}
                    subtitle={_('Fechas de cada estadio y período crítico según la fecha de siembra.')}
                >
                    <PhenologyTimeline hybrids={phenologyHybrids} />
                    <PhenologyLegend />
                </Section>

                <Divider />

                <Section
                    title={_('Requerimiento nutricional')}
                    subtitle={_(
                        'Nutrientes totales que necesitan los híbridos seleccionados para el rendimiento estimado.'
                    )}
                >
                    <NutrientTable hybrids={hybrids} />
                </Section>
            </Stack>

            {recommendation?.id && (
                <RecommendationCommentsDrawer
                    open={commentsDrawerOpen}
                    setCommentsDrawerOpen={setCommentsDrawerOpen}
                    recommendationId={recommendation.id}
                />
            )}
        </PageContainer>
    );
}

export default RecommendationResultV2;
